import moment from 'moment';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { useGetConversationMessagesQuery } from '@/api/messagesApi';
import type { DefaultUserSettings } from '@/configs/defaultUserSettings';
import { useActiveProfile, useTokenState } from '@/store/localStorage/useTokenState';
import { selectUserSettingsAugmented } from '@/store/user/selectors/selectUserSettingsAugmented';

const GROUP_TIME_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

export function useConversationMessages() {
  const { targetProfileId } = useParams<{ targetProfileId: string }>();
  const { activeProfileId } = useActiveProfile();
  const settings = useSelector(selectUserSettingsAugmented) as DefaultUserSettings;

  const pageSize = settings.pagination.comments;
  const page =
    Number(new URLSearchParams(useLocation().search).get('page')) ||
    Number(new URLSearchParams(useLocation().search).get('p')) ||
    1;

  console.log('useConversationMessages', { targetProfileId, activeProfileId, page, pageSize });

  const [token] = useTokenState();
  const isLoggedIn = Boolean(token);

  const { data: messagesData } = useGetConversationMessagesQuery(
    {
      ownProfileId: activeProfileId || '',
      targetProfileId: targetProfileId || '',
      pageNumber: page,
      pageSize: pageSize
    },
    {
      skip: !isLoggedIn || !activeProfileId || !targetProfileId
    }
  );

  const messages = isLoggedIn ? [...(messagesData?.data || [])] : [];
  const totalMessages = isLoggedIn ? messagesData?.meta?.totalMessages || 0 : 0;

  const messageGroups = useMemo(() => {
    const groups: { messages: typeof messages; showDateSeparator: boolean }[] = [];
    messages.forEach((message, index) => {
      const previousMessage = index > 0 ? messages[index - 1] : null;
      const showDateSeparator =
        !previousMessage || !moment(message.createdAt).isSame(moment(previousMessage.createdAt), 'day');
      const timeDiff = previousMessage
        ? moment(message.createdAt).diff(moment(previousMessage.createdAt), 'milliseconds')
        : Infinity;
      const isNewGroup =
        !previousMessage ||
        message.sender.id !== previousMessage.sender.id ||
        showDateSeparator ||
        timeDiff > GROUP_TIME_WINDOW_MS;

      if (isNewGroup) {
        groups.push({ messages: [message], showDateSeparator });
      } else {
        groups[groups.length - 1].messages.push(message);
      }
    });
    return groups;
  }, [messages]);

  return {
    totalMessages,
    messageGroups,
    activeProfileId,
    page,
    pageSize,
    targetProfileId
  };
}
